import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { gql } from "@apollo/client";
import type { SyncableNonPharmaItem } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { NonPharmaItem as RxNonPharmaItem } from "../collections/non-pharma-item";
import type { RxDBCollections } from "../provider";
import {
  SyncNonPharmaItemPullDocument,
  type WholeSyncableNonPharmaItemFragment,
} from "./__generated__/non-pharma-item.gql";
import { useGqlReplication } from "./use-gql-replication";

export const useReplicateNonPharmaItem = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxNonPharmaItem>;
}) => {
  const [enabled] = useFeatureFlag<boolean>(featureFlags.clinical.nonPharma);

  const pull = useMemo(() => {
    if (!enabled) return undefined;

    return {
      query: SyncNonPharmaItemPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
        reSyncInterval: { hours: 1 },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
    };
  }, [enabled]);

  return useGqlReplication<RxNonPharmaItem, SyncableNonPharmaItem, null>({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlNonPharmaItemToRxNonPharmaItem,
    batchSize: 1000,
    metadataLocalStorageKeySuffix: "non-pharma-item",
  });
};

const mapGqlNonPharmaItemToRxNonPharmaItem = (
  item: WholeSyncableNonPharmaItemFragment,
): WithDeleted<RxNonPharmaItem> => ({
  id: item.id,
  title: item.title,
  brand: item.brand ?? null,
  categoryId: item.categoryId,
  snomedId: item.snomedId ?? null,
  keywords: [...item.keywords],
  productType: item.productType,
  meta: {
    createdAt: item.meta.createdAt,
    createdByUserId: item.meta.createdByUserId,
    updatedAt: item.meta.updatedAt,
    updatedByUserId: item.meta.updatedByUserId,
  },
  _deleted: item.meta.deleted,
});

gql`
  fragment WholeSyncableNonPharmaItem on SyncableNonPharmaItem {
    id
    title
    brand
    categoryId
    snomedId
    keywords
    productType
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncNonPharmaItemPull($input: PullNonPharmaItemInput!) {
    pullNonPharmaItem(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableNonPharmaItem
      }
    }
  }
`;
